import { useMemo, useState, type FormEvent } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function SearchPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const q = params.get('q') || '';
  const [keyword, setKeyword] = useState(q);

  const results = useMemo(() => {
    const kw = q.trim().toLowerCase();
    if (!kw) return [];
    return comics.filter(
      (c) =>
        c.title.toLowerCase().includes(kw) ||
        c.category.toLowerCase().includes(kw) ||
        c.author.toLowerCase().includes(kw) ||
        c.tags.some((t) => t.toLowerCase().includes(kw))
    );
  }, [q]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    if (keyword.trim()) {
      navigate(`/search?q=${encodeURIComponent(keyword.trim())}`);
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
        <nav aria-label="面包屑" className="mb-5 text-sm text-foreground-500">
          <ol className="flex flex-wrap items-center gap-1.5">
            <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
            <li><i className="ri-arrow-right-s-line"></i></li>
            <li><span className="text-foreground-700">搜索结果</span></li>
          </ol>
        </nav>

        <header className="max-w-2xl">
          <h1 className="font-display text-2xl text-foreground-900 md:text-3xl">站内搜索</h1>
          <p className="mt-2 text-sm text-foreground-600">输入漫画名称、作者或分类关键词，快速定位你想追的漫画。</p>
          <form onSubmit={handleSearch} className="mt-5 flex items-center gap-2 rounded-full border border-background-200 bg-background-100 p-2 focus-within:border-primary-300">
            <i className="ri-search-line pl-3 text-foreground-500"></i>
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索漫画名称 / 作者 / 分类"
              aria-label="搜索关键词"
              className="flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button type="submit" className="rounded-full bg-primary-500 px-5 py-2 text-sm text-background-50 hover:bg-primary-600 whitespace-nowrap">
              搜索
            </button>
          </form>
        </header>

        <div className="mt-8">
          {q ? (
            <>
              <p className="text-sm text-foreground-600">
                关键词「<strong className="text-primary-600">{q}</strong>」共找到 <strong>{results.length}</strong> 部作品
              </p>
              {results.length > 0 ? (
                <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
                  {results.map((c) => (
                    <ComicCard key={c.id} comic={c} />
                  ))}
                </div>
              ) : (
                <div className="mt-10 rounded-lg border border-background-200 bg-background-100 p-10 text-center">
                  <i className="ri-search-eye-line text-4xl text-foreground-300"></i>
                  <p className="mt-3 text-foreground-700">没有找到与「{q}」相关的漫画</p>
                  <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或浏览热门分类。</p>
                  <div className="mt-5 flex flex-wrap justify-center gap-2">
                    {categories.slice(0, 6).map((c) => (
                      <Link key={c.name} to={`/comics?category=${encodeURIComponent(c.name)}`} className="rounded-full bg-secondary-100 px-4 py-2 text-sm text-secondary-900 hover:bg-secondary-200">
                        {c.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="rounded-lg border border-background-200 bg-background-100 p-10 text-center">
              <p className="text-foreground-700">请输入关键词开始搜索</p>
            </div>
          )}
        </div>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'SearchResultsPage',
              name: '搜索结果 - 4399漫画网',
              isPartOf: { '@type': 'WebSite', name: '4399漫画网' },
            }),
          }}
        />
      </div>
    </Layout>
  );
}